// Events Service - Fully migrated to SAM infrastructure

import { apiClient } from "@/lib/api/client";
import { API_CONFIG } from "@/lib/api/config";

// Updated event types based on new API structure
export interface Event {
  entity_id: string;
  name: string;
  description?: string;
  location?: string;
  category?: string;
  entity_type: string; // Always "Eventish" for events
  calendar_data: {
    dtstart: string; // ISO 8601 start date/time
    dtend?: string;
    dtstart_local?: string;
    dtstart_utc?: string;
    dtend_local?: string;
    dtend_utc?: string;
    all_day?: boolean;
    calendar_summary: string; // Calendar event title
    event_type: "single_event" | "recurring_event" | "multi_date_event";
    calendar_enabled: boolean; // Always true for events API
  };
  date: string; // Legacy date field (same as dtstart)
  id: string; // Legacy ID field (same as entity_id)

  // Optional fields that may be present
  image_url?: string;
  url?: string;
  source?: string;
  created_at?: string;
  updated_at?: string;
  all_day?: boolean; // Indicates if this is an all-day event
  linked_venue_id?: string; // ID of the venue associated with this event
}

export interface EventsResponse {
  events: Event[];
  eventsCount: number; // Number of events in this response (for pagination)
  total: number; // Total events found
  days?: number; // Search window in days
  limit?: number;
  offset?: number;
  cursor?: string | null;
  hasMore?: boolean;
}

export interface EventsQueryParams {
  limit?: number;
  offset?: number;
  cursor?: string | null;
  category?: string;
  start_date?: string;
  end_date?: string;
  location?: string;
  sort?: string;
}

export interface EventMessage {
  id: string;
  event_id: string;
  message_text: string;
  created_at: string;
}

export interface EventCategory {
  name: string;
  count: number;
  description?: string;
}

export interface EventCategoriesResponse {
  categories: EventCategory[];
  total: number;
}

export interface EventSearchResponse extends EventsResponse {
  query: string;
  took?: number; // Search time in ms
}

export interface UpcomingEventsResponse extends EventsResponse {
  days?: number; // Search window in days
}

export interface PastEventsResponse extends EventsResponse {
  days: number;
  cutoff_date: string;
}

export interface RelatedEventsResponse {
  event_id: string;
  related_events: Event[];
  total: number;
}

class EventsService {
  private static instance: EventsService;

  static getInstance(): EventsService {
    if (!EventsService.instance) {
      EventsService.instance = new EventsService();
    }
    return EventsService.instance;
  }

  getConfig() {
    return {
      version: "SAM",
      baseUrl: API_CONFIG.BASE_URL,
    };
  }

  // Core SAM endpoints using shared apiClient
  async getEvents(params: EventsQueryParams = {}, signal?: AbortSignal): Promise<EventsResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.offset) queryParams.append("offset", params.offset.toString());
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.category) queryParams.append("category", params.category);
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);
    if (params.location) queryParams.append("location", params.location);
    if (params.sort) queryParams.append("sort", params.sort);

    const endpoint = `/events${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    return apiClient.request<EventsResponse>(endpoint, { method: "GET", signal });
  }

  async getEvent(id: string): Promise<Event> {
    return apiClient.get<Event>(`/events/${id}`);
  }

  async batchGetEvents(ids: string[]): Promise<{ events: Record<string, Event>; not_found: string[] }> {
    return apiClient.post<{ events: Record<string, Event>; not_found: string[] }>("/events/batch", { ids });
  }

  async getEventCategories(): Promise<EventCategoriesResponse> {
    return apiClient.get<EventCategoriesResponse>("/events/categories");
  }

  async getEventsByCategory(
    category: string,
    params: Omit<EventsQueryParams, "category"> = {},
  ): Promise<EventsResponse> {
    return this.getEvents({ ...params, category });
  }

  async searchEvents(query: string, params: Omit<EventsQueryParams, "search"> = {}): Promise<EventSearchResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append("q", query);
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.offset) queryParams.append("offset", params.offset.toString());
    if (params.category) queryParams.append("category", params.category);

    const endpoint = `/events/search?${queryParams.toString()}`;
    return apiClient.get<EventSearchResponse>(endpoint);
  }

  async getUpcomingEvents(days: number | null = 7): Promise<UpcomingEventsResponse> {
    // Use timezone-aware date range instead of UTC-based /events/upcoming
    // This respects the user's local timezone
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const hasWindow = typeof days === "number" && Number.isFinite(days);
    let endDate: Date | null = null;

    if (hasWindow) {
      endDate = new Date(startOfToday);
      endDate.setDate(endDate.getDate() + days);
    }

    const response = await this.getEvents({
      start_date: this.toLocalISOString(startOfToday),
      ...(hasWindow && endDate ? { end_date: this.toLocalISOString(endDate) } : {}),
      sort: "date_asc",
    });

    // Transform to match UpcomingEventsResponse format
    return {
      ...response,
      days: hasWindow ? days : undefined,
    };
  }

  /**
   * Converts a Date to ISO string format with local timezone offset
   * Example: "2025-01-14T00:00:00-08:00" for PST
   * This ensures the API receives dates in the user's actual timezone
   */
  private toLocalISOString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    // Get timezone offset in minutes and convert to ±HH:MM format
    const offset = -date.getTimezoneOffset();
    const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
    const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, "0");
    const offsetSign = offset >= 0 ? "+" : "-";

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
  }

  async getPastEvents(days: number = 30): Promise<PastEventsResponse> {
    const endpoint = `/events/past?days=${days}`;
    return apiClient.get<PastEventsResponse>(endpoint);
  }

  async getRelatedEvents(eventId: string): Promise<RelatedEventsResponse> {
    return apiClient.get<RelatedEventsResponse>(`/events/related/${eventId}`);
  }

  async getVenueEvents(
    venueId: string,
    params: {
      limit?: number;
      start_date?: string;
      end_date?: string;
      sort?: "date_asc" | "date_desc";
    } = {},
  ): Promise<EventsResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);
    if (params.sort) queryParams.append("sort", params.sort);

    const endpoint = `/venues/${venueId}/events${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    return apiClient.get<EventsResponse>(endpoint);
  }
}

// Export singleton instance
export const eventsService = EventsService.getInstance();

// Also export class for testing
export default EventsService;
